import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Warning as WarningIcon } from '@mui/icons-material';
import React from 'react';

interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  /** When true, confirm button shows loading and is disabled (caller closes dialog after success) */
  confirmLoading?: boolean;
  /** Optional error message to show in the dialog (e.g. API failure) */
  error?: string | null;
}

export function DeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLoading = false,
  error = null,
}: DeleteConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: '#FFF3E0',
          py: 2,
          px: 3,
        }}
      >
        <WarningIcon sx={{ color: '#F57C00', fontSize: 28 }} />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '18px',
            fontWeight: 500,
            color: '#212121',
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            color: '#4A5565',
            mb: 2,
          }}
        >
          {message}
        </Typography>
        {error && (
          <Box
            sx={{
              bgcolor: '#FFEBEE',
              border: '1px solid #E57373',
              borderRadius: '4px',
              p: 1.5,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '13px',
                color: '#C62828',
              }}
            >
              {error}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            bgcolor: '#E8F5E9',
            border: '1px solid #81C784',
            borderRadius: '4px',
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '13px',
              color: '#2E7D32',
            }}
          >
            <strong>Note:</strong> This item will be moved to the Recycle Bin and can be restored from the Recycle Bin tab. It will be retained for 60 days; after that, it will be permanently deleted automatically.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            border: '0.8px solid #E5E7EB',
            borderRadius: '8px',
            color: '#4A5565',
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            px: 3,
            '&:hover': {
              bgcolor: '#F5F5F5',
              border: '0.8px solid #E5E7EB',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onConfirm();
            if (!confirmLoading) onClose();
          }}
          disabled={confirmLoading}
          sx={{
            bgcolor: '#D32F2F',
            border: '0.8px solid #D32F2F',
            borderRadius: '8px',
            color: '#fff',
            textTransform: 'none',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            px: 3,
            '&:hover': {
              bgcolor: '#B71C1C',
              border: '0.8px solid #B71C1C',
            },
            '&:disabled': {
              bgcolor: '#9E9E9E',
              borderColor: '#9E9E9E',
              color: '#fff',
            },
          }}
        >
          {confirmLoading ? 'Deleting...' : 'Move to Recycle Bin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
